/**
 * SYSTEM ADMIN (Master-GSA-only) DTOs — Gsa / User / Credential / Bank — migration guide
 * Bölüm 3.7 & 3.10, 4.2/4.10. Cross-checked against the real API's live swagger doc
 * (`/swagger/docs/v1`, 219 documented paths total) + curl against a live session. See
 * api/endpoints/system.ts for the per-route confirmation notes; summary here:
 *
 * - Gsa: `GetGsaList`, `GetAllGsas`, `AddGsa`, `EditGsa`, `DeleteGsa` all exist and are
 *   confirmed against the code-traced `Docs/api/05-corporate.md` (§GsaController). Only
 *   `Gsa/UploadLogo` has no route (logo stays a web-tier file op). `GsaAddRequest`'s fields
 *   are FLAT on the request body (not wrapped under a `gsa` key).
 * - User: fully confirmed. `UserRequest` wraps a `UserModel` under a `user` key; the doc's
 *   `UserAuth` sub-object (IsPrebookingAllowed etc.) does NOT exist on the real `UserModel` —
 *   those flags only exist on the currently-authenticated user (CurrentUser / api/types/auth.ts),
 *   not as a per-managed-user editable permission set. Real field list confirmed via swagger's
 *   `UserModel` definition AND a live `User/GetUsers` call (see endpoints file).
 * - Credential ("Provider Ayarları" in the UI): RE-VERIFIED 2026-08-10 against a locally-running
 *   backend instance (`http://localhost:1538/swagger/docs/v1`) — the controller DOES exist there
 *   (it's genuinely absent from the shared live test host, testbookingapiv2.arkman.com.tr, which
 *   is why the earlier pass concluded it didn't exist at all). Confirmed via swagger schema +
 *   live curl (`Credential/GetCredentials` against a real session returned real rows). Request
 *   wraps the entity under a `credential` key exactly as previously guessed, but several fields
 *   were missing/wrong — see `Credential` below for the corrected shape. Response field is
 *   `credentials` (curl-confirmed), not the previously-guessed `credentialList`.
 * - Bank ("Banka Ayarları"): same story as Credential — RE-VERIFIED 2026-08-10 against
 *   `http://localhost:1538/`, confirmed via swagger + live curl (`Bank/GetBanks` returned real
 *   rows). Response field is `banks` (curl-confirmed), not the previously-guessed `bankList`;
 *   the entity's real fields are `bankName`/`posCode`, not the previously-guessed `name`/`code`.
 */
import type { Currency, ProviderType, ResponseBase } from './common';

/* --------------------------------- Gsa --------------------------------- */

export type GsaLanguage = 0 | 1;
export type GsaMoneyType = 0 | 1 | 2 | 3;
export type GsaType = 'AGENCY' | 'CORPORATE' | 'MASTER';

/** Confirmed via swagger's `BusinessObjects.Gsa` definition — the shape returned in list rows. */
export interface Gsa {
  id: number;
  name?: string;
  language?: GsaLanguage;
  gsaType?: GsaType;
  moneyType?: GsaMoneyType;
  city?: string;
  district?: string;
  zipCode?: string;
  countryCode?: string;
  address?: string;
  phone?: string;
  gsm?: string;
  email?: string;
  taxOffice?: string;
  taxNo?: string;
  currentCode?: string;
  coperCode?: string;
  isActive?: boolean;
  logo?: string;
  masterGsaId?: number;
}

/** Flat fields, no `gsa` wrapper. Field set per the code-traced `Docs/api/05-corporate.md`
 *  (`GsaAddRequest`, §Gsa/AddGsa): the profile fields below plus `currentCode`, `gsaType`
 *  (int? → `GsaType` enum, default AGENCY), `masterGsaId` (int?), `defaultBank` (→ Mongo
 *  `MappedBank.Default`), `mappedBanks` (→ Mongo `MappedBank.Banks`). ZipCode/Phone/
 *  CoperCode/IsActive/Logo/MoneyType/Language are not settable on create. */
export interface GsaAddRequest {
  name?: string;
  city?: string;
  district?: string;
  countryCode?: string;
  address?: string;
  gsm?: string;
  email?: string;
  taxOffice?: string;
  taxNo?: string;
  currentCode?: string;
  gsaType?: number;
  masterGsaId?: number;
  defaultBank?: string;
  mappedBanks?: string[];
}

/** `Gsa/EditGsa` AND `Gsa/DeleteGsa` — CONFIRMED against live swagger (2026-08-27): both
 *  take the full `GsaEditRequest` object (Delete keys off `id`). Fields verified 1:1:
 *  `{ id, name, currentCode, city, district, countryCode, address, gsm, email, taxOffice,
 *  taxNo, isActive, logo, gsaType (int), masterGsaId (int), defaultBank, mappedBanks[] }`. */
export interface GsaEditRequest {
  id: number;
  name?: string;
  currentCode?: string;
  city?: string;
  district?: string;
  countryCode?: string;
  address?: string;
  gsm?: string;
  email?: string;
  taxOffice?: string;
  taxNo?: string;
  isActive?: boolean;
  logo?: string;
  gsaType?: number;
  masterGsaId?: number;
  defaultBank?: string;
  mappedBanks?: string[];
}

/** RE-VERIFIED 2026-08-10 via live curl against local-backend (`http://localhost:1538/`):
 *  `Gsa/GetGsaList` returns `{ GsaList: [...], HasError }` → camelCase `gsaList`, confirming
 *  the earlier guessed field name was right. The route's earlier failures were caused by a
 *  request-shape bug, not a backend defect — see api/endpoints/system.ts's `getGsaList`
 *  header for the full story (the request body needs `{sessionHeader}`, wrapped — a bare
 *  SessionHeader body left the server's `request.SessionHeader` null). */
export interface GsaListResponse extends ResponseBase {
  gsaList?: Gsa[];
}

/* --------------------------------- User --------------------------------- */

/** Real API's `UserModel.UserType` is a plain int32 (not the string enum used elsewhere in this
 *  app for login's `UserType`). Mapping below is INFERRED by cross-referencing live
 *  `User/GetUsers` data against `common.ts`'s `UserType` string-literal order — e.g. the row for
 *  username `ArkSystem` has `UserType: 3` and is clearly a SYSTEM account; GSA users
 *  (`Arkgsa`, `Capados`) both have `UserType: 2`. Not verified against backend source, so
 *  treat with caution — flagged clearly in the form UI. */
export type UserTypeCode = 0 | 1 | 2 | 3 | 4;

export const USER_TYPE_LABELS: Record<UserTypeCode, string> = {
  0: 'B2C',
  1: 'B2E',
  2: 'GSA',
  3: 'SYSTEM',
  4: 'SP',
};

/** Confirmed via swagger's `UserModel` definition AND a live `User/GetUsers` call — every
 *  field below was observed on real response rows. */
export interface SystemUser {
  id: number;
  gsaId?: number;
  username?: string;
  arrangerCode?: string;
  expenseCenter?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  userType?: UserTypeCode;
  isActive?: boolean;
  currency?: string;
  language?: string;
  isTwoFactorAuthenticationActive?: boolean;
  /** Write-only on create: ask the API to generate a password instead of accepting `password`. */
  generatePassword?: boolean;
  /** Write-only — real GetUsers responses always return `null` here even for existing users, so
   *  never pre-fill an edit form from a fetched row. */
  password?: string;
  threeDPayment?: boolean;
  creditCardPayment?: boolean;
  currentPayment?: boolean;
  isDefaultServiceFee?: boolean;
  isExpCenterCreditAllowed?: boolean;
  fixServiceFee?: number;
  fixServiceFeePercent?: number;
  flexServiceFee?: number;
  flexServiceFeePercent?: number;
  flexServiceFeeCurrency?: number;
  extraDomFee?: number;
  extraDomFeePercent?: number;
  extraIntFee?: number;
  extraIntFeePercent?: number;
  agencyCode?: string;
  isChangesAllowed?: boolean;
}

/** Confirmed live: `User/GetUsers` returns exactly `{ hasError, credentials, generatedPassword,
 *  userList }`. */
export interface UserListResponse extends ResponseBase {
  userList?: SystemUser[];
  credentials?: unknown[] | null;
  generatedPassword?: string | null;
}

/* ------------------------------ Credential ------------------------------
 * Confirmed via local-backend swagger + live curl — see file header.
 */

/** Confirmed via swagger's `Credential` definition enum — which specific supplier/
 *  distribution-channel integration this credential set belongs to (distinct from
 *  `providerType`, which is the service category — Air/Hotel/etc). */
export type CredentialProvider =
  | 'Amadeus'
  | 'Galileo'
  | 'Sabre'
  | 'TurkishAirlines'
  | 'OnurAir'
  | 'AtlasGlobal'
  | 'Pegasus'
  | 'SunExpress'
  | 'BoraJet'
  | 'Arkman'
  | 'Travelrobot'
  | 'BiletBank'
  | 'Iati'
  | 'Paxdrive'
  | 'HotelRunner'
  | 'AerTicket'
  | 'Paximum'
  | 'Coper'
  | 'Gulf'
  | 'MetroBus'
  | 'Corendon'
  | 'Ajet'
  | 'RateHawk'
  | 'Turna'
  | 'Yolcu360'
  | 'Freebird'
  | 'THYNDC'
  | 'ArkmanTour'
  | 'Tamamliyo'
  | 'Travelport'
  | 'Namila'
  | 'None';

export type CredentialSalesType = 'B2E' | 'B2C';

/** Confirmed via swagger's `Auth.Credential` definition + a live `GetCredentials` call.
 *  `providerType` reuses common.ts's shared `ProviderType` — its enum values are
 *  identical to swagger's own `Credential.ProviderType` enum. `gsaName` (previously
 *  guessed) does not exist on the real entity — only `gsaId` + a nested `gsa` object. */
export interface Credential {
  id: number;
  provider?: CredentialProvider;
  providerType?: ProviderType;
  salesType?: CredentialSalesType;
  currency?: Currency;
  providerName?: string;
  /** .NET assembly name for this provider's integration — confirmed present in swagger,
   *  not previously modeled. */
  providerAssemblyName?: string;
  endPointName?: string;
  address?: string;
  serviceUsername?: string;
  /** Sensitive — mask in list views, treat as write-only in the edit form (never pre-fill). */
  servicePassword?: string;
  /** Sensitive — same treatment as `servicePassword`. */
  serviceSecurityKey?: string;
  creditCard?: string;
  officeId?: string;
  isProviderServiceFee?: boolean;
  isActive?: boolean;
  isTest?: boolean;
  prebookingAllowed?: boolean;
  bookingAllowed?: boolean;
  gsaId?: number;
  gsa?: Gsa;
  insertedAt?: string;
  updatedAt?: string;
}

/** Confirmed via live curl: `Credential/GetCredentials` returns `{ Credentials, HasError }`
 *  → camelCase `credentials` (previously guessed as `credentialList`). Add/Edit/Delete
 *  respond with the same shape (the refreshed list), confirmed via live curl. */
export interface CredentialListResponse extends ResponseBase {
  credentials?: Credential[];
}

/* --------------------------------- Bank ---------------------------------
 * Confirmed via local-backend swagger + live curl — see file header.
 */

/** Confirmed via swagger's `BusinessObjects.Bank` definition + a live `GetBanks` call. */
export interface Bank {
  id: number;
  bankName?: string;
  posCode?: string;
  currency?: Currency;
  isActive?: boolean;
  isDefault?: boolean;
  gsaId?: number;
  gsa?: Gsa;
  insertedAt?: string;
  updatedAt?: string;
}

/** Confirmed via live curl: `Bank/GetBanks` returns `{ Banks, HasError }` → camelCase
 *  `banks` (previously guessed as `bankList`). Add/Edit/Delete respond with the same
 *  shape (the refreshed list), confirmed via live curl. */
export interface BankListResponse extends ResponseBase {
  banks?: Bank[];
}
